/**
 * resolveSkinFromAncestors — shared skin-name resolution (RUN-514).
 *
 * Walks the COMPOSED ancestor chain looking for a `data-run-skin` attribute,
 * crossing shadow-DOM boundaries via `getRootNode().host`. Returns `'default'`
 * when none is found.
 *
 * This replaces the hand-copied `parentElement`-only walk that 35 components
 * each carried privately. `parentElement` returns `null` at a shadow-root
 * boundary, so the old walk could not see a host-document `data-run-skin` from
 * inside a shadow root — every nested/composed component silently fell back to
 * `default`, so non-default skins (e.g. wireframe) never reached composed UI.
 * The canonical crossing logic already exists in `skin-resolution-mixin.js`
 * (`_resolveSkinName`); this is the standalone form for components that have
 * not yet migrated to the mixin.
 *
 * Callers keep their own explicit-skin check (e.g. `this.getAttribute('skin')`
 * or a private `#skin` field) and delegate only the ancestor walk here.
 *
 * @param {Element} start - the component element to resolve a skin for.
 * @returns {string} the nearest ancestor `data-run-skin`, or `'default'`.
 */
export function resolveSkinFromAncestors(start) {
  let node = start;
  while (node) {
    let parent = node.parentElement;
    if (!parent) {
      // Cross the shadow boundary: the host of this node's root, if any.
      const root = node.getRootNode && node.getRootNode();
      parent = root && root.host ? root.host : null;
    }
    if (!parent) break;
    const v = parent.getAttribute && parent.getAttribute('data-run-skin');
    if (v) return v;
    node = parent;
  }
  return 'default';
}
